import { IAccount } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { computed, makeObservable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	IUserSessionComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import { IEventLoggingComponentProps, withEventLogging } from '../../../../models/Logging';
import { isValidEmail } from '../../../../models/UiUtils';
import { AccountUserViewModel } from '../../../../viewmodels/AdminViewModels';
import { Modal } from '../../../../web/components/Modal';
import { TextInput } from '../../../../web/components/TextInput';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { styleSheet } from './styles';

interface IProps extends IUserSessionComponentProps, IEventLoggingComponentProps, IErrorMessageComponentProps {
	className?: string;
	onRequestClose?(result?: unknown, canceled?: boolean): void;
	isOpen: boolean;
	styles?: StyleDeclarationValue[];
	userVM: AccountUserViewModel;
	account: IAccount;
	type: EmailChangeType;
}

interface IState {
	email: string;
	reason: string;
}

export enum EmailChangeType {
	Authenticator = 'Authenticator',
	User = 'User',
}

class _EmailChangeModal extends React.Component<IProps, IState> {
	public constructor(props: IProps) {
		super(props);

		makeObservable<_EmailChangeModal, 'accountUserViewModel'>(this, {
			accountUserViewModel: computed,
		});

		this.state = {
			email: '',
			reason: '',
		};
	}

	public render() {
		const { className, onRequestClose, styles, type } = this.props;
		return (
			<Modal
				className={`${css(styleSheet.container, ...(styles || []))} component-class ${className || ''}`}
				isOpen={!!this.props.isOpen}
				onRequestClose={onRequestClose}
				useDefaultHeader={true}
			>
				<div>
					{type === EmailChangeType.Authenticator ? 'Change Two-Factor Authentication Email' : 'Change User Email'}
				</div>
				<div className={css(styleSheet.inputRow)}>
					<TextInput
						autoComplete='off'
						className={css(styleSheet.textBox)}
						disabled={this.accountUserViewModel.isBusy}
						inputId='email'
						onChange={this.onChangeEmail}
						placeholder='New Email Address'
						type='text'
					/>
					<TextInput
						autoComplete='off'
						className={css(styleSheet.textBox)}
						disabled={this.accountUserViewModel.isBusy}
						inputId='reason'
						onChange={this.onChangeReason}
						placeholder='What is your reason?'
						type='text'
					/>
					<button
						className={css(baseStyleSheet.brandLink)}
						onClick={this.onChange}
						disabled={this.accountUserViewModel.isBusy}
					>
						Change
					</button>
				</div>
			</Modal>
		);
	}

	private get accountUserViewModel() {
		const { userSession, userVM } = this.props;
		// creating a new view model because impersonating pollutes the original view model
		// must be computed because userVM will be null in the constructor

		return new AccountUserViewModel(userSession, userVM);
	}

	private onChange = () => {
		const { email, reason } = this.state;
		const { logApiError, logEvent, errorMessages, onRequestClose, type } = this.props;

		logEvent(`Changing${type}Email`);
		if (!isValidEmail(email)) {
			toast.error('Please include a valid email');
			return;
		}
		this.accountUserViewModel.impersonate({
			account: { id: this.accountUserViewModel.accountId },
			user: { id: this.accountUserViewModel.id },
		});
		this.accountUserViewModel
			.changeEmail(email, reason, type)
			.then(() => {
				toast.success('Email successfully changed.');

				onRequestClose();
			})
			.catch(error => {
				errorMessages.pushApiError(error);

				logApiError(`Changing${type}Email-Error`, error);
			});
	};

	private onChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => this.setState({ email: event.target.value });
	private onChangeReason = (event: React.ChangeEvent<HTMLInputElement>) =>
		this.setState({ reason: event.target.value });
}

const EmailChangeModalAsObserver = observer(_EmailChangeModal);
const EmailChangeModalWithContext = inject(
	UserSessionViewModelKey,
	ErrorMessagesViewModelKey
)(EmailChangeModalAsObserver);
export const EmailChangeModal = withEventLogging(EmailChangeModalWithContext, 'EmailChangeModal');
